<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps<{
  data: number[];
  labels: string[];
  color?: string;
  windowSize?: number;
}>();

const canvas = ref<HTMLCanvasElement | null>(null);
const color = props.color ?? '#111111';
const WINDOW_SIZE = props.windowSize ?? 10;

// Fenêtre glissante : offset = index de départ dans les données complètes
const offset = ref(0);

const maxOffset = computed(() => Math.max(0, props.data.length - WINDOW_SIZE));
const canPaginate = computed(() => props.data.length > WINDOW_SIZE);
const windowData = computed(() =>
  canPaginate.value
    ? props.data.slice(offset.value, offset.value + WINDOW_SIZE)
    : props.data,
);
const windowLabels = computed(() =>
  canPaginate.value
    ? props.labels.slice(offset.value, offset.value + WINDOW_SIZE)
    : props.labels,
);

function goPrev() {
  offset.value = Math.max(0, offset.value - WINDOW_SIZE);
}
function goNext() {
  offset.value = Math.min(maxOffset.value, offset.value + WINDOW_SIZE);
}

// Recale la fenêtre sur les données les plus récentes quand la série change
watch(
  () => props.data.length,
  () => {
    offset.value = maxOffset.value;
  },
  { immediate: true },
);

function drawChart() {
  const el = canvas.value;
  if (!el) return;
  const ctx = el.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const W = el.offsetWidth;
  const H = el.offsetHeight;
  el.width = W * dpr;
  el.height = H * dpr;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, W, H);

  const pad = { top: 16, right: 16, bottom: 32, left: 48 };
  const w = W - pad.left - pad.right;
  const h = H - pad.top - pad.bottom;
  const data = windowData.value;
  const n = data.length;
  if (n < 2) return;

  const maxVal = Math.max(...data);
  const minVal = Math.min(...data);
  const range = maxVal - minVal || 1;

  const xStep = w / (n - 1);
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const textColor = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';

  // Grid lines (5 horizontal)
  ctx.strokeStyle = gridColor;
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const y = pad.top + (h / 4) * i;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(pad.left + w, y);
    ctx.stroke();
    const val = Math.round(maxVal - (range / 4) * i);
    ctx.fillStyle = textColor;
    ctx.font = '10px system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(
      val >= 1000 ? `${(val / 1000).toFixed(0)}k` : String(val),
      pad.left - 6,
      y + 4,
    );
  }

  // X labels — un label par point (fenêtre limitée à WINDOW_SIZE points)
  ctx.textAlign = 'center';
  const minLabelSpacing = 44; // px minimum entre deux labels
  const maxLabels = Math.max(2, Math.floor(w / minLabelSpacing));
  const labelStep = Math.max(1, Math.ceil((n - 1) / (maxLabels - 1)));
  windowLabels.value.forEach((lbl, i) => {
    if (i % labelStep !== 0 && i !== n - 1) return;
    const x = pad.left + i * xStep;
    ctx.fillStyle = textColor;
    ctx.font = '9px system-ui, sans-serif';
    ctx.fillText(lbl, x, H - 8);
  });

  const toXY = (i: number) => ({
    x: pad.left + i * xStep,
    y: pad.top + h - ((data[i] - minVal) / range) * h,
  });

  // Gradient fill
  const gradient = ctx.createLinearGradient(0, pad.top, 0, pad.top + h);
  gradient.addColorStop(
    0,
    color
      .replace(')', ', 0.2)')
      .replace('rgb', 'rgba')
      .replace('#111111', 'rgba(0,0,0,0.2)'),
  );
  gradient.addColorStop(
    1,
    color
      .replace(')', ', 0)')
      .replace('rgb', 'rgba')
      .replace('#111111', 'rgba(0,0,0,0)'),
  );

  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const { x, y } = toXY(i);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.lineTo(pad.left + (n - 1) * xStep, pad.top + h);
  ctx.lineTo(pad.left, pad.top + h);
  ctx.closePath();
  ctx.fillStyle = gradient;
  ctx.fill();

  // Line
  ctx.beginPath();
  ctx.strokeStyle = color.includes('#') ? color : color;
  ctx.lineWidth = 2.5;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  for (let i = 0; i < n; i++) {
    const { x, y } = toXY(i);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.stroke();

  // Last dot
  const last = toXY(n - 1);
  ctx.beginPath();
  ctx.arc(last.x, last.y, 4, 0, Math.PI * 2);
  ctx.fillStyle = '#111111';
  ctx.fill();
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 2;
  ctx.stroke();
}

let ro: ResizeObserver | null = null;
onMounted(() => {
  drawChart();
  ro = new ResizeObserver(() => drawChart());
  if (canvas.value?.parentElement) ro.observe(canvas.value.parentElement);
});
onUnmounted(() => ro?.disconnect());
watch(() => props.data, drawChart, { deep: true });
watch(offset, drawChart);
</script>

<template>
  <div class="chart-wrap">
    <div v-if="canPaginate" class="chart-nav">
      <button
        class="chart-nav-btn"
        :disabled="offset === 0"
        @click="goPrev"
        aria-label="Période précédente"
      >
        <i class="pi pi-chevron-left" />
      </button>
      <span class="chart-nav-label">
        {{ windowLabels[0] }} → {{ windowLabels[windowLabels.length - 1] }}
      </span>
      <button
        class="chart-nav-btn"
        :disabled="offset === maxOffset"
        @click="goNext"
        aria-label="Période suivante"
      >
        <i class="pi pi-chevron-right" />
      </button>
    </div>
    <canvas ref="canvas" class="chart-canvas" />
  </div>
</template>

<style scoped>
.chart-wrap {
  width: 100%;
  height: 220px;
  position: relative;
  display: flex;
  flex-direction: column;
}
.chart-canvas {
  width: 100%;
  flex: 1;
  min-height: 0;
  display: block;
}
.chart-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding-bottom: 0.375rem;
  flex-shrink: 0;
}
.chart-nav-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  min-width: 8rem;
  text-align: center;
}
.chart-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.03);
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.625rem;
  cursor: pointer;
  transition: all 0.15s;
}
.chart-nav-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.08);
  color: #0b1320;
}
.chart-nav-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

[data-theme='dark'] .chart-nav-label {
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .chart-nav-btn {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .chart-nav-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}
</style>
